// translations/pages/kyveil/nl.js

export const kyveilPageNL = {
  "seo": {
    "title": "Kyveil PII-anonimisering | pAIoneer Labs GmbH",
    "description": "Kyveil herkent en anonimiseert persoonsgegevens lokaal op uw systemen. Desktopapplicatie of REST-API voor PII-anonimisering conform de AVG.",
    "keywords": "Kyveil, PII-anonimisering, persoonsgegevens anonimiseren, privacy, gegevensbescherming, AVG, GDPR, Microsoft Presidio, spaCy, flair, lokale gegevensverwerking",
    "ogTitle": "Kyveil: professionele PII-anonimisering met AI en NLP",
    "ogDescription": "Automatische herkenning en anonimisering van persoonsgegevens als desktopapplicatie of REST-API op uw eigen infrastructuur.",
    "twitterTitle": "Kyveil PII-anonimisering",
    "twitterDescription": "Lokale PII-herkenning en -anonimisering voor gevoelige documenten.",
    "url": "https://paioneer-labs.eu/kyveil",
    "structuredData": {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Kyveil",
      "applicationCategory": "SecurityApplication",
      "operatingSystem": "Windows, macOS, Linux, Server",
      "description": "Software voor het lokaal herkennen en anonimiseren van persoonsgegevens met AI en NLP.",
      "provider": {
        "@type": "Organization",
        "name": "pAIoneer Labs GmbH",
        "url": "https://paioneer-labs.eu"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "url": "https://paioneer-labs.eu/kyveil"
      }
    }
  },
  "hero": {
    "eyebrow": "Gegevensbescherming en anonimisering",
    "title": "Kyveil",
    "subtitle": "Lokale anonimisering vóór AI, tests en uitwisseling.",
    "description": "Kyveil is een lokale anonimiseringslaag voor documenten en AI-workflows. De software herkent persoonsgegevens in Word-, Excel-, PDF- en tekstbestanden, vervangt of maskeert deze en toont een voorbeeldweergave voordat inhoud naar AI-chats, tests, analyses of externe partijen gaat. Kyveil wordt [IDD-gedreven](idd) (Intent-Driven Development) en geautomatiseerd met [Veridion](veridion) ontwikkeld en continu doorontwikkeld door onze AI-medewerkers met mensen in de loop.",
    "platforms": "Desktopapplicatie, REST-API en AnonChat",
    "proofPoints": [
      "Documenten blijven lokaal op uw systemen.",
      "Persoonsgegevens worden vervangen of gemaskeerd.",
      "Een voorbeeldweergave toont de geanonimiseerde inhoud vóór uitwisseling of AI-chat.",
      "Word, Excel, PDF en tekst zijn de centrale documentworkflows."
    ]
  },
  "dataSovereignty": {
    "title": "Uw gegevens. Uw systemen. Uw controle.",
    "subtitle": "Volledige datasoevereiniteit door lokale verwerking.",
    "items": [
      {
        "title": "Geen cloud nodig",
        "description": "Alle verwerking vindt lokaal plaats op uw eigen infrastructuur. Uw bestanden verlaten uw systemen nooit. Geen afhankelijkheid van de cloud, geen externe gegevensoverdracht."
      },
      {
        "title": "Edge computing",
        "description": "De desktopapplicatie draait volledig zelfstandig op werkstations. De API draait op uw eigen servers. Beide varianten verwerken alle gegevens lokaal binnen uw beveiligde omgeving."
      },
      {
        "title": "Data-autonomie",
        "description": "Volledige controle over uw gegevensstromen. Geen vendor lock-in. Geen doorlopende cloudkosten. Uw gevoelige informatie blijft uitsluitend onder uw controle."
      },
      {
        "title": "Standaardhardware",
        "description": "Geen GPU nodig. Draait op gangbare kantoorcomputers en standaardservers. Investeringen in gespecialiseerde hardware zijn niet nodig."
      }
    ]
  },
  "features": {
    "title": "Kernfuncties",
    "subtitle": "Alles wat nodig is voor gecontroleerde PII-herkenning en -anonimisering in bedrijfsprocessen.",
    "items": [
      {
        "title": "AI-gebaseerde PII-herkenning",
        "description": "Automatische herkenning met de Microsoft Presidio SDK, spaCy van Explosion AI en flair van de Humboldt-Universität zu Berlin.",
        "tone": "primary"
      },
      {
        "title": "Replace-modus",
        "description": "Vervangt herkende PII door generieke placeholders zoals [NAAM], [EMAIL] en [ADRES] voor compliancescenario's.",
        "tone": "info"
      },
      {
        "title": "Fake-modus",
        "description": "Vervangt herkende PII door plausibele vervangende waarden voor testomgevingen en ontwikkeling.",
        "tone": "warning"
      },
      {
        "title": "Meerdere documentformaten",
        "description": "Verwerkt Microsoft Word, Excel, OpenDocument, PDF-bestanden en platte tekst.",
        "tone": "success"
      },
      {
        "title": "Meertalige ondersteuning",
        "description": "Ingebouwde ondersteuning voor Duits, Engels, Frans, Italiaans en Spaans met taalspecifieke NLP-modellen.",
        "tone": "secondary"
      },
      {
        "title": "Configureerbare herkenning",
        "description": "Bepaal welke PII-entiteiten worden herkend: namen, adressen, e-mailadressen, telefoonnummers, bankrekeningen, belastingnummers en meer.",
        "tone": "primary"
      },
      {
        "title": "Uitbreidbaarheid",
        "description": "Interfaces voor eigen pre- en postprocessinglogica, voor integratie in uw specifieke workflows.",
        "tone": "info"
      },
      {
        "title": "Geoptimaliseerd voor CPU",
        "description": "De efficiënte modelarchitectuur draait op standaard kantoorhardware en vereist geen GPU.",
        "tone": "warning"
      }
    ]
  },
  "technology": {
    "title": "Technologiestack",
    "subtitle": "Frameworks van enterpriseniveau en bewezen NLP-onderzoek.",
    "items": [
      {
        "title": "Microsoft Presidio SDK",
        "description": "Microsoft Data Protection and De-identification SDK. Framework dat geldt als industriestandaard voor PII-herkenning en -anonimisering, met ingebouwde entity recognizers.",
        "tone": "primary"
      },
      {
        "title": "spaCy",
        "subtitle": "Ontwikkeld door Explosion AI",
        "description": "Industrial-Strength Natural Language Processing. Productierijpe NLP-bibliotheek met voorgetrainde statistische modellen voor named entity recognition.",
        "tone": "info"
      },
      {
        "title": "flair",
        "subtitle": "Ontwikkeld door de Humboldt-Universität zu Berlin",
        "description": "State-of-the-art NLP-framework met contextuele string-embeddings. Geavanceerde neurale netwerkmodellen voor een nauwkeurigere herkenning van entiteiten.",
        "tone": "secondary"
      }
    ],
    "integration": "Geïntegreerde AI-pipeline",
    "integrationDesc": "Gecombineerde herkenning met Presidio als orkestratielaag en met spaCy- en flair-modellen, voor optimale herkenning in meerdere talen en voor meerdere entiteitstypen."
  },
  "deployment": {
    "title": "Deploymentopties",
    "subtitle": "Kies de oplossing die bij uw toepassing past.",
    "options": [
      {
        "title": "Desktopapplicatie",
        "subtitle": "Zelfstandige applicatie voor individuele werkplekken",
        "description": "De desktopversie draait volledig zelfstandig op Windows, macOS of Linux. Ideaal voor gebruikers die documenten direct op hun werkplek moeten anonimiseren, zonder afhankelijkheid van het netwerk.",
        "features": [
          "100% offline te gebruiken - geen internetverbinding nodig",
          "Bestanden verlaten het lokale werkstation nooit",
          "Batchverwerking voor meerdere bestanden",
          "Grafische gebruikersinterface voor eenvoudige bediening",
          "Draait op standaard kantoorcomputers zonder GPU",
          "Eenmalige licentie - geen abonnement"
        ]
      },
      {
        "title": "REST-API",
        "subtitle": "Serverdeployment voor integratie en schaalbaarheid",
        "description": "De API-versie draait op uw eigen servers of op een private-cloudinfrastructuur. Ideaal om anonimisering te integreren in bestaande systemen en workflows met grote verwerkingsvolumes.",
        "features": [
          "Deployment op eigen infrastructuur - on-premises of in een private cloud",
          "Horizontale schaling voor hoge doorvoereisen",
          "RESTful API met uitgebreide documentatie",
          "Dezelfde anonimiseringsengine als de desktopversie",
          "Integratievoorbeelden voor gangbare platforms",
          "Ondersteuning voor aanpassingen in pre- en postprocessing"
        ]
      },
      {
        "title": "AnonChat",
        "subtitle": "AI-webchat met voorafgaande anonimisering",
        "description": "AnonChat plaatst Kyveil vóór de eigenlijke AI-chat. Word- en Excel-documenten worden geanonimiseerd, een voorbeeldweergave wordt gecontroleerd en pas daarna gaat de inhoud naar ChatGPT, Claude, Gemini of interne LLM's.",
        "features": [
          "Anonimisering vóór de chat in plaats van controle achteraf",
          "Voorbeeldweergave van de geanonimiseerde tekst vóór de overdracht",
          "Geschikt voor documentgebaseerde vragen aan AI-systemen",
          "Verkleint het risico dat persoonsgegevens naar externe modellen worden gestuurd",
          "Kan worden gekoppeld aan interne LLM-omgevingen of chatomgevingen van ondernemingen",
          "Gebruikt dezelfde anonimiseringsaanpak als de desktopapplicatie en de REST-API"
        ]
      }
    ],
    "bothNote": "Desktopapplicatie, REST-API en AnonChat zijn ontworpen om gevoelige inhoud gecontroleerd te anonimiseren vóór verdere verwerking."
  },
  "technicalDetails": {
    "title": "Technische mogelijkheden",
    "subtitle": "Configureerbare herkenning voor Europese bedrijfsdocumenten.",
    "items": [
      {
        "title": "Taalondersteuning",
        "description": "Voorgetrainde NLP-modellen voor Duits, Engels, Frans, Italiaans en Spaans. Taalspecifieke entiteitsherkenning voor nauwkeurige PII-herkenning in een Europese zakelijke context.",
        "tone": "primary"
      },
      {
        "title": "Configureerbare PII-entiteiten",
        "description": "Kies welke soorten persoonsgegevens worden herkend en geanonimiseerd: persoonsnamen, e-mailadressen, telefoonnummers, postadressen, IBAN-rekeningnummers, creditcardnummers, socialezekerheidsnummers, belastingnummers, paspoortnummers en meer.",
        "tone": "info"
      },
      {
        "title": "Anonimiseringsmodi",
        "description": "De replace-modus vervangt PII door generieke placeholders voor compliancedocumentatie. De fake-modus gebruikt plausibele vervangende waarden voor testomgevingen, met behoud van de documentstructuur.",
        "tone": "warning"
      },
      {
        "title": "Pre- en postprocessing",
        "description": "Implementeer eigen logica vóór en na de anonimisering. Voeg via de meegeleverde interfaces documentvalidatie, formaatconversie, kwaliteitscontroles, auditlogging of integratie met achterliggende systemen toe.",
        "tone": "secondary"
      }
    ]
  },
  "anonChat": {
    "title": "AnonChat breidt Kyveil uit met een AI-webchat",
    "subtitle": "De nieuwe app past dezelfde anonimiseringsaanpak toe vóór de eigenlijke chat met externe of interne AI-systemen.",
    "eyebrow": "Nieuwe toepassing",
    "name": "AnonChat",
    "description": "AnonChat is een AI-webchat waarin Word- en Excel-documenten kunnen worden geanonimiseerd voordat ze worden gebruikt met ChatGPT, Claude, Gemini of interne LLM's. Een voorbeeldweergave toont de geanonimiseerde tekst voordat de inhoud naar de chat gaat.",
    "statement": "Zo ligt de beslissing bij de gebruiker: controleren, vrijgeven en dan pas chatten.",
    "steps": [
      {
        "title": "Document laden",
        "description": "Word- of Excel-bestanden worden voorbereid op anonimisering voordat ze in de AI-workflow terechtkomen.",
        "tone": "primary"
      },
      {
        "title": "PII anonimiseren",
        "description": "Kyveil herkent persoonsgegevens en vervangt of maskeert ze volgens de gekozen regels.",
        "tone": "info"
      },
      {
        "title": "Voorbeeldweergave controleren",
        "description": "De geanonimiseerde tekst is zichtbaar voordat de inhoud aan de chat wordt doorgegeven.",
        "tone": "warning"
      },
      {
        "title": "AI-chat gebruiken",
        "description": "Pas na de controle wordt de geanonimiseerde inhoud gebruikt voor de chat met externe of interne LLM's.",
        "tone": "success"
      }
    ]
  },
  "useCases": {
    "title": "Toepassingen",
    "subtitle": "Van AVG-compliance tot veilige ontwikkeldata.",
    "items": [
      {
        "title": "AVG-compliance",
        "description": "Anonimiseer persoonsgegevens voordat documenten extern worden gedeeld of binnen interne afdelingen worden gebruikt. Voldoe aan de eisen voor gegevensbescherming bij testdata, analysedatasets en documentatie.",
        "tone": "primary"
      },
      {
        "title": "Voorbereiding van testdata",
        "description": "Bereid realistische testdatasets zo voor dat ontwikkelaars kunnen werken met data die op productiedata lijken, zonder echte persoonlijke informatie prijs te geven.",
        "tone": "info"
      },
      {
        "title": "Verwerking van HR-documenten",
        "description": "Anonimiseer sollicitaties, personeelsdossiers en HR-documentatie voor onbevooroordeelde beoordelingen, compliance-audits of data-analyses, met behoud van privacy.",
        "tone": "warning"
      },
      {
        "title": "Data-analyse",
        "description": "Maak de analyse van documenten met PII mogelijk door gevoelige informatie vooraf te anonimiseren. Deel datasets tussen teams zonder zorgen over privacy.",
        "tone": "success"
      },
      {
        "title": "Documentarchivering",
        "description": "Bereid documenten voor op langdurige archivering door PII te verwijderen of te anonimiseren volgens bewaarrichtlijnen en voorschriften voor gegevensbescherming.",
        "tone": "secondary"
      },
      {
        "title": "Veilig delen",
        "description": "Deel documenten met externe partners, opdrachtnemers of andere afdelingen nadat gevoelige persoonlijke informatie automatisch is verwijderd.",
        "tone": "error"
      }
    ]
  },
  "releases": {
    "title": "Varianten en ondersteuning",
    "subtitle": "Updates, uitbreidingen en ondersteuning voor productief gebruik.",
    "items": [
      {
        "title": "Desktop, API en AnonChat",
        "description": "Kyveil is beschikbaar als desktopapplicatie en als REST-API voor integraties. AnonChat is de webchat met voorafgaande documentanonimisering.",
        "tone": "info"
      },
      {
        "title": "Ondersteuning",
        "description": "Voor gebruik in bedrijven bieden wij technische ondersteuning, hulp bij de implementatie en aanpassingen.",
        "tone": "success"
      }
    ]
  },
  "cta": {
    "title": "Aan de slag met Kyveil",
    "subtitle": "Bescherm gevoelige documenten voordat ze in AI, tests, analyses of externe uitwisseling terechtkomen.",
    "desktop": "Desktopapplicatie aanvragen",
    "desktopSubject": "Kyveil Desktop",
    "apiContact": "Contact over deployment van de REST-API",
    "contact": "Schrijf ons voor een demo of een offerte.",
    "email": "info@paioneer.eu",
    "apiSubject": "Kyveil REST API Deployment",
    "contactSubject": "Kyveil-demo of offerte"
  }
};
